import React, { useState, useEffect } from "react";
import {
  Editor,
  EditorState,
  ContentState,
  ContentBlock,
  convertFromHTML,
  DefaultDraftBlockRenderMap,
  RichUtils,
} from "draft-js";
import { Map as ImmutableMap } from "immutable";
import { stateToHTML } from "draft-js-export-html";
// import EditorToolbar from "../Report/EditorToolbar";

const blockRenderMap = ImmutableMap({
  "header-one": {
    element: "h1",
    wrapper: <div className="text-4xl font-bold" />,
  },
  "header-two": {
    element: "h2",
    wrapper: <div className="text-3xl font-bold" />,
  },
  "header-three": {
    element: "h3",
    wrapper: <div className="text-2xl font-bold" />,
  },
  "header-four": {
    element: "h4",
    wrapper: <div className="text-xl font-bold" />,
  },
  "header-five": {
    element: "h5",
    wrapper: <div className="text-lg font-bold" />,
  },
  "header-six": {
    element: "h6",
    wrapper: <div className="text-base font-bold" />,
  },
  blockquote: {
    element: "blockquote",
    wrapper: <div className="border-l-4 pl-2" />,
  },
  "code-block": { element: "pre" },
  "unordered-list-item": { element: "li", wrapper: <ul /> },
  "ordered-list-item": { element: "li", wrapper: <ol /> },
  "custom-div": { element: "div" },
});

const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);

const customBlockRenderers = {
  "custom-div": (block: ContentBlock) => {
    const id = block.getData().get("id");
    return `<div id="${id}">${block.getText()}</div>`;
  },
};

const parseCustomHTML = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const blocks: ContentBlock[] = [];

  Array.from(doc.body.childNodes).forEach((node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      if (element.tagName.toLowerCase() === "div" && element.id) {
        blocks.push(
          new ContentBlock({
            key: element.id,
            type: "custom-div",
            text: element.textContent || "",
            data: ImmutableMap({ id: element.id }),
          })
        );
      } else {
        const { contentBlocks } = convertFromHTML(element.outerHTML);
        blocks.push(...contentBlocks);
      }
    }
  });

  return ContentState.createFromBlockArray(blocks);
};

interface DocxEditorProps {
  initialContent: string;
  setEditorContent: (content: string) => void;
  onClose: () => void;
  isOpen: boolean;
}

const DocxEditor: React.FC<DocxEditorProps> = ({
  initialContent,
  setEditorContent,
  onClose,
  isOpen,
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (initialContent) {
      const contentState = parseCustomHTML(initialContent);
      setEditorState(EditorState.createWithContent(contentState));
    }
  }, [initialContent]);

  const handleEditorChange = (state: EditorState) => {
    setEditorState(state);
    setIsDirty(true);

    const contentState = state.getCurrentContent();

    if (contentState.hasText()) {
      const htmlContent = stateToHTML(contentState, {
        blockRenderers: customBlockRenderers,
      });
      setEditorContent(htmlContent);
    }
  };

  const handleKeyCommand = (command: string, state: EditorState) => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      handleEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  // const toggleBlockType = (blockType: string) => {
  //     handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  // };
  //
  // const toggleInlineStyle = (inlineStyle: string) => {
  //     handleEditorChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  // };
  //
  // const undo = () => {
  //     setEditorState(EditorState.undo(editorState));
  // };
  //
  // const redo = () => {
  //     setEditorState(EditorState.redo(editorState));
  // };

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black z-50 transition-opacity duration-300 ease-out ${
        isOpen ? "opacity-100 bg-opacity-50" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="bg-white rounded-lg shadow-lg w-1/2 h-2/3 flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">File preview</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <Editor
            editorState={editorState}
            onChange={handleEditorChange}
            handleKeyCommand={handleKeyCommand}
            blockRenderMap={extendedBlockRenderMap}
          />
        </div>
      </div>
    </div>
  );
};

export default DocxEditor;
